/**
 * Voice narration of "Make it a story", played from ElevenLabs audio that the
 * server makes (and caches). The browser only fetches MP3 clips from our own
 * API; the key never reaches it.
 *
 * Each clip is requested once and kept as a blob URL, so pausing, replaying
 * and the next line's prefetch are instant.
 *
 * One clip plays at a time. Its state (requested → playing → done, or paused,
 * blocked, error) is tracked per label ("opening", "line:<id>", "closing"),
 * so the film can wait for a line to finish before moving on, and the dev
 * status line can show what happened.
 */

export type NarrationPart = 'opening' | 'closing' | 'line';

export interface ClipRequest {
  episodeId: string;
  part: NarrationPart;
  /** Required for "line": the memory the line belongs to. */
  mediaId?: string;
  lang: 'en' | 'ur';
  /** ElevenLabs voice id, or null for the listener's saved narrator. */
  voice: string | null;
  /** The clip's version (its text+voice hash, or the story's generatedAt): new text never reuses an old clip. */
  version?: string;
}

/** requested = fetching; blocked = the browser refused to play without a tap. */
export type ClipState = 'requested' | 'playing' | 'paused' | 'blocked' | 'done' | 'error';

export interface NarrationSnapshot {
  /** Label of the clip asked for last, e.g. "opening", "line:<mediaId>", "closing". */
  current: string | null;
  /** Language of the current clip. */
  lang: 'en' | 'ur' | null;
  state: ClipState | null;
  error: string | null;
  /** Every label seen since the film started, with its last state. */
  history: Array<{ label: string; state: ClipState }>;
  /** Labels whose voice has really started playing at least once. */
  started: string[];
}

const clips = new Map<string, Promise<string>>();
const audio: HTMLAudioElement | null = typeof Audio !== 'undefined' ? new Audio() : null;
/** Bumped on every request, so a slow clip never plays after a newer one was asked for. */
let latest = 0;
let paused = false;
let current: string | null = null;
let currentLang: 'en' | 'ur' | null = null;
let requestedAt = 0;
let lastError: string | null = null;
const history = new Map<string, ClipState>();
const started = new Set<string>();
const listeners = new Set<() => void>();
/** A clip that has not arrived after this long no longer holds the film. */
const REQUEST_TIMEOUT_MS = 12000;

export const labelOf = (r: Pick<ClipRequest, 'part' | 'mediaId'>) => (r.part === 'line' ? `line:${r.mediaId}` : r.part);

function set(label: string, state: ClipState) {
  history.set(label, state);
  if (state === 'playing') started.add(label);
  listeners.forEach((fn) => fn());
}

if (audio) {
  audio.addEventListener('ended', () => {
    if (current) set(current, 'done');
  });
  audio.addEventListener('playing', () => {
    if (current) set(current, 'playing');
  });
}

const keyOf = (r: ClipRequest) => [r.episodeId, r.part, r.mediaId ?? '', r.lang, r.voice ?? '', r.version ?? ''].join('|');

function urlFor(r: ClipRequest): string {
  const q = new URLSearchParams({ part: r.part, lang: r.lang });
  if (r.mediaId) q.set('mediaId', r.mediaId);
  if (r.voice) q.set('voice', r.voice);
  if (r.version) q.set('v', r.version);
  return `/api/narration/${r.episodeId}?${q.toString()}`;
}

function clip(r: ClipRequest): Promise<string> {
  const key = keyOf(r);
  let found = clips.get(key);
  if (!found) {
    found = fetch(urlFor(r), { headers: { 'X-Memoryflix-Client': '1' } }).then(async (res) => {
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Narration failed (${res.status})`);
      }
      return URL.createObjectURL(await res.blob());
    });
    // A failed clip is forgotten, so the next try asks again.
    found.catch(() => clips.delete(key));
    clips.set(key, found);
  }
  return found;
}

function start(label: string) {
  if (!audio) return;
  void audio.play().then(
    () => set(label, 'playing'),
    (err: DOMException) => {
      // Autoplay rules: the clip waits for a tap ("Tap for sound") instead of being lost.
      if (err?.name === 'NotAllowedError') set(label, 'blocked');
    },
  );
}

/**
 * Start reading a clip (replaces whatever is playing). While paused it is
 * loaded and starts on `resumeClip`. Errors are reported to `onError`.
 */
export function playClip(r: ClipRequest, onError?: (message: string) => void): void {
  if (!audio) return;
  const token = ++latest;
  const label = labelOf(r);
  // Only asked for while the film plays; a later pause holds it again.
  paused = false;
  audio.pause();
  current = label;
  currentLang = r.lang;
  requestedAt = performance.now();
  lastError = null;
  set(label, 'requested');
  clip(r)
    .then((url) => {
      if (token !== latest) return;
      audio.src = url;
      if (paused) set(label, 'paused');
      else start(label);
    })
    .catch((err: Error) => {
      if (token !== latest) return;
      lastError = err.message;
      set(label, 'error');
      onError?.(err.message);
    });
}

/** Fetch a clip ahead of time so it starts without a pause. */
export function prefetchClip(r: ClipRequest): void {
  clip(r).catch(() => undefined);
}

export function pauseClip(): void {
  paused = true;
  audio?.pause();
  if (current && history.get(current) === 'playing') set(current, 'paused');
}

/** Carry on after a pause, or after the tap that allows sound. */
export function resumeClip(): void {
  paused = false;
  if (!audio || !current) return;
  const state = history.get(current);
  if ((state === 'paused' || state === 'blocked') && audio.src) start(current);
}

export function stopClip(): void {
  latest++;
  audio?.pause();
  if (current && history.get(current) !== 'done' && history.get(current) !== 'error') history.delete(current);
  current = null;
  listeners.forEach((fn) => fn());
}

/** Forget what was played (a new film or a replay). */
export function resetNarration(): void {
  stopClip();
  paused = false;
  history.clear();
  started.clear();
  listeners.forEach((fn) => fn());
}

/**
 * True while the clip with this label still has words to say: being fetched,
 * playing, paused, or waiting for a tap. The film waits for it before moving on.
 */
export function clipBusy(label: string): boolean {
  if (current !== label) return false;
  const state = history.get(label);
  if (state === 'requested') return performance.now() - requestedAt < REQUEST_TIMEOUT_MS;
  return state === 'playing' || state === 'paused' || state === 'blocked';
}

export function narrationSnapshot(): NarrationSnapshot {
  return {
    current,
    lang: currentLang,
    state: current ? (history.get(current) ?? null) : null,
    error: lastError,
    history: [...history].map(([label, state]) => ({ label, state })),
    started: [...started],
  };
}

export function onNarrationChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
